import InfoModal from '@/components/molecules/InfoModal';
import ACCreationForm, { ACCreationFormHandle } from '@/components/organisms/ACCreationForm';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { accountingCenterService } from '@/services/accounting-centers.service';
import { budgetMemberService } from '@/services/budget-member.service';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import ACCreationSteps from '@/components/molecules/accountingCenter/ACCreationSteps';
import ACCreationStepsMobile from '@/components/molecules/accountingCenter/ACCreationStepsMobile';
import ACMemberCalculationsFooter from '@/components/molecules/accountingCenter/ACMemberCalculationsFooter';
import { Button } from '@/components/ui/button';
import { budgetService } from '@/services/budget.service';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { getCurrentUser } from '@/store/slices/auth';
import {
  AccountingCenter,
  AccountingCenterFormValues,
  AccountingCenterMemberFormValues,
  AccountingCenterUpdateWithAllocationsData
} from '@/services/types/accounting-centers-types';
import { ACMemberCalculations } from '@/utils/accountingCenterCalculations';
import {
  getChangedACMemberAllocations,
  getACFormMaxValues,
  getDefaultACPool,
  mapBudgetMembersToACEditMembers,
  resolveDefaultAccountingCenter
} from '@/utils/accountingCenterMembers';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

const formatACTitle = (acType?: string) =>
  acType
    ? acType
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : '';

const ACEditPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [maxExpenseValue, setMaxExpenseValue] = useState(0);
  const [maxRevenueValue, setMaxRevenueValue] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [memberCalculations, setMemberCalculations] = useState<ACMemberCalculations | null>(null);
  const [accountingCenter, setAccountingCenter] = useState<AccountingCenter | null>(null);
  const [isDefaultAC, setIsDefaultAC] = useState(false);
  const [initialMembers, setInitialMembers] = useState<AccountingCenterMemberFormValues[]>([]);

  const navigate = useNavigate();
  const { budgetId, acType, acId } = useParams();
  const currentUser = useSelector(getCurrentUser);

  const formRef = useRef<ACCreationFormHandle>(null);
  const initialMembersRef = useRef<AccountingCenterMemberFormValues[]>([]);

  const membersStepEnabled = !isDefaultAC;
  const isLastStep = !membersStepEnabled || activeStep === 2;

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/accounting-centers/${acType}/${acId}`,
      // This page is always pushed from the AC detail page above, so cancelling
      // should pop back to it rather than replace (which would create a
      // duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  const formDefaultValues = useMemo(
    (): Partial<AccountingCenterFormValues> => ({
      name: accountingCenter?.name ?? '',
      description: accountingCenter?.description ?? '',
      acmev: accountingCenter?.acmev ?? 0,
      acmev_alert_threshold: accountingCenter?.acmev_alert_threshold ?? 0,
      acrt: accountingCenter?.acrt ?? 0,
      acrt_alert_threshold: accountingCenter?.acrt_alert_threshold ?? 0
    }),
    [accountingCenter]
  );

  const fetchInitialData = async () => {
    if (!budgetId || !acId) {
      setLoadError('Accounting center ID is missing');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError(null);

      const acTypeUpper = String(acType ?? '').toUpperCase();
      const [budgetResponse, membersResponse, acResponse, acsResponse] = await Promise.all([
        budgetService.getBudget(budgetId),
        budgetMemberService.getMembers(budgetId),
        accountingCenterService.getAccountingCenter(budgetId, acId),
        accountingCenterService.getAccountingCentersByType(budgetId, acTypeUpper)
      ]);

      const budget = budgetResponse.data;
      const acData = acResponse.data;
      const defaultAC = resolveDefaultAccountingCenter(acsResponse.data ?? []);
      const pool = getDefaultACPool(defaultAC, budget);
      const maxValues = getACFormMaxValues(pool, acData.is_default ? null : acData);

      setCurrency(budget.currency ?? 'EUR');
      setAccountingCenter(acData);
      setIsDefaultAC(acData.is_default === true);
      setMaxExpenseValue(maxValues.acmev);
      setMaxRevenueValue(maxValues.acrt);

      if (!acData.is_default) {
        const members = mapBudgetMembersToACEditMembers(
          membersResponse.data ?? [],
          acData.members ?? [],
          defaultAC?.members ?? []
        );
        setInitialMembers(members);
        initialMembersRef.current = members;
      } else {
        setInitialMembers([]);
        initialMembersRef.current = [];
      }
    } catch (err: any) {
      console.error('Error loading accounting center:', err);
      setLoadError(err?.message ?? 'Failed to load accounting center');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [budgetId, acId, acType]);

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      if (!budgetId || !acId) {
        throw new Error('Budget ID and accounting center ID are required');
      }

      let payload: AccountingCenterUpdateWithAllocationsData;

      if (isDefaultAC) {
        payload = {
          description: data.description,
          acmev_alert_threshold: Number(data.acmev_alert_threshold),
          acrt_alert_threshold: Number(data.acrt_alert_threshold)
        };
      } else {
        const memberAllocations = getChangedACMemberAllocations(
          formRef.current?.getMembers() ?? [],
          initialMembersRef.current
        );

        payload = {
          name: data.name,
          description: data.description,
          acmev: Number(data.acmev),
          acmev_alert_threshold: Number(data.acmev_alert_threshold),
          acrt: Number(data.acrt),
          acrt_alert_threshold: Number(data.acrt_alert_threshold),
          ...(memberAllocations.length > 0 ? { member_allocations: memberAllocations } : {})
        };
      }

      await accountingCenterService.updateAccountingCenter(budgetId, acId, payload);

      toast.success('Accounting center updated successfully');
      allowNavigation();
      // This page was pushed from the AC detail page, so pop back to it
      // instead of navigating to the list — replacing to the same URL as the
      // previous history entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (err: any) {
      console.error('Error updating accounting center:', err);
      toast.error(err?.message ?? 'Failed to update accounting center. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRightButtonClick = async () => {
    if (activeStep === 1) {
      const isValid = await formRef.current?.validateStep(1);
      if (!isValid) {
        return;
      }

      if (membersStepEnabled) {
        setActiveStep(2);
        return;
      }

      formRef.current?.submit();
      return;
    }

    const isValid = await formRef.current?.validateStep(2);
    if (isValid) {
      formRef.current?.submit();
    }
  };

  const handleLeftButtonClick = () => {
    if (activeStep === 2) {
      setActiveStep(1);
      return;
    }
    handleCancel();
  };

  if (isLoading) {
    return (
      <div className="border flex-1 rounded-[16px] p-4 bg-white min-h-[320px] flex items-center justify-center gap-2">
        <Loader2 className="size-5 animate-spin text-primary" />
        <p className="text-secondary text-sm">Loading accounting center...</p>
      </div>
    );
  }

  if (loadError || !accountingCenter) {
    return (
      <div className="border flex-1 rounded-[16px] p-4 bg-white min-h-[320px] flex flex-col items-center justify-center gap-3">
        <p className="text-red-500 text-sm text-center">{loadError ?? 'Failed to load accounting center'}</p>
        <Button variant="primary-reverse" className="rounded-full px-5" onClick={fetchInitialData}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar title={<span className="capitalize">{acType?.split('_')[0]} Accounting Center Edit</span>} />

      <div className="flex-1 bg-[#FBFBFF] p-4 md:bg-white rounded-[16px]">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-3 md:mb-0">
          <h2 className="text-center font-bold mb-1 md:text-left md:text-[24px] md:mb-6">
            Edit your {formatACTitle(acType)} Accounting Center
          </h2>

          <ACCreationSteps activeStep={activeStep} showMembersStep={membersStepEnabled} className="shrink-0 mb-6" />
        </div>

        <div className="md:hidden mb-6">
          <ACCreationStepsMobile activeStep={activeStep} showMembersStep={membersStepEnabled} />
        </div>

        <ACCreationForm
          ref={formRef}
          activeStep={activeStep}
          onSubmit={handleSubmit}
          defaultValues={formDefaultValues}
          isEditMode
          isDefaultAC={isDefaultAC}
          maxExpenseValue={maxExpenseValue}
          maxRevenueValue={maxRevenueValue}
          currency={currency}
          isSubmitting={isSubmitting}
          initialMembers={initialMembers}
          currentUserEmail={currentUser?.email}
          currentUserPhotoUrl={currentUser?.photoURL}
          onMemberCalculationsChange={setMemberCalculations}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
        />
      </div>

      <footer className="flex flex-col md:flex-row items-end justify-between gap-4 px-4 py-7 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent">
        <div className="flex-1 w-full md:w-auto min-w-0">
          {activeStep === 2 && membersStepEnabled && <ACMemberCalculationsFooter calculations={memberCalculations} />}
        </div>
        <div className="w-full md:w-auto flex justify-end gap-4 shrink-0 mt-2 md:mt-0">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant="text"
            onClick={handleLeftButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {activeStep === 1 ? 'Cancel' : 'Previous'}
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleRightButtonClick}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? 'Saving...' : isLastStep ? 'Save' : 'Next'}
          </Button>
        </div>
      </footer>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your accounting center"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}
    </>
  );
};

export default ACEditPage;
